import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  ArrowLeft,
  Trash2,
  Package2,
  ShoppingCart,
  Minus,
  Plus,
  CheckCircle2,
  AlertCircle,
  Gift,
  TrendingUp,
  Truck,
  Tag,
  X,
  Lock,
  ChevronRight,
  Sparkles,
  RefreshCw,
  Pencil,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useToast } from "../context/ToastContext";
import Price from "../components/Currency/Price";
import { ImageWithFallback } from "../constants/image-placeholder";
import ConfirmModal from "../components/Modals/Confirm";
import { useCart, calculateShippingProgress } from "./cart.utils";
import BaseApi from "../services/base-api";
import Mastercard from "../images/mastercard.png";
import Visa from "../images/visa.png";
import applePay from "../images/apple-pay.png";
import googlePay from "../images/google-pay.png";
import "../styles/cart-animations.css";
import GiftSendModal, {
  GiftModalFormData,
} from "../components/Product/GiftSendModal";
import LoadingComponent from "../components/LoadingComponent";
import { COUNTRY_CODES } from "../constants/countryCodes";

const extractVariationAttributes = (variation: any): Record<string, string> => {
  if (!variation) return {};

  const parsedVariation =
    typeof variation === "string"
      ? (() => {
          try {
            return JSON.parse(variation);
          } catch {
            return null;
          }
        })()
      : variation;

  const attributes: Record<string, string> = {
    ...(parsedVariation?.attributes || {}),
  };

  if (
    !attributes.color &&
    (parsedVariation?.color || parsedVariation?.colour)
  ) {
    attributes.color = parsedVariation.color || parsedVariation.colour;
  }

  if (!attributes.size && parsedVariation?.size) {
    attributes.size = parsedVariation.size;
  }

  if (!attributes.style && parsedVariation?.style) {
    attributes.style = parsedVariation.style;
  }

  return attributes;
};

const extractVariationId = (variation: any): string | null => {
  if (!variation) return null;

  const parsedVariation =
    typeof variation === "string"
      ? (() => {
          try {
            return JSON.parse(variation);
          } catch {
            return null;
          }
        })()
      : variation;

  return (
    parsedVariation?.id ||
    parsedVariation?._id ||
    parsedVariation?.variationId ||
    variation?.id ||
    variation?._id ||
    null
  );
};

/* ==================== VARIATION DISPLAY ==================== */
const VariationDisplay = React.memo(
  ({ variation, onEdit }: { variation: any; onEdit?: () => void }) => {
    const { t } = useTranslation();
    if (!variation) return null;

    const parsedVariation =
      typeof variation === "string"
        ? (() => {
            try {
              return JSON.parse(variation);
            } catch {
              return null;
            }
          })()
        : variation;

    const attributes = parsedVariation?.attributes || {};
    const colorValue =
      attributes.color ||
      attributes.colour ||
      parsedVariation?.color ||
      parsedVariation?.colour;
    const sizeValue = attributes.size || parsedVariation?.size;
    const styleValue = attributes.style || parsedVariation?.style;

    return (
      <div className="flex flex-wrap items-center gap-2 mb-2">
        {colorValue && (
          <div className="flex items-center gap-1.5 bg-gray-100 px-2.5 py-1 rounded-md">
            <span className="text-xs font-medium text-gray-600">
              {t("Color")}:
            </span>
            <div
              className="w-5 h-5 rounded-full border-2 border-gray-300"
              style={{ backgroundColor: colorValue }}
            />
          </div>
        )}
        {sizeValue && (
          <div className="flex items-center gap-1.5 bg-gray-100 px-2.5 py-1 rounded-md">
            <span className="text-xs font-medium text-gray-600">
              {t("Size")}:
            </span>
            <span className="text-xs font-semibold text-gray-900">
              {sizeValue}
            </span>
          </div>
        )}
        {styleValue && (
          <div className="flex items-center gap-1.5 bg-gray-100 px-2.5 py-1 rounded-md">
            <span className="text-xs font-medium text-gray-600">
              {t("Style")}:
            </span>
            <span className="text-xs font-semibold text-gray-900">
              {styleValue}
            </span>
          </div>
        )}
        {onEdit && (
          <button
            onClick={onEdit}
            className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors"
            aria-label={t("Edit variation")}
          >
            <Pencil size={14} />
          </button>
        )}
      </div>
    );
  },
);

const CartItem = React.memo(
  ({
    item,
    index,
    isRemoving,
    isUpdating,
    onUpdateQuantity,
    onRemove,
    onEditVariation,
  }: any) => {
    const { t, i18n } = useTranslation();
    const [localQuantity, setLocalQuantity] = useState(item.quantity);
    // Sync local quantity with prop changes from server
    useEffect(() => {
      setLocalQuantity(item.quantity);
    }, [item.quantity]);

    // Safely extract values from the new API structure
    const stock = item.availableStock ?? item.product?.stock ?? 0;
    const price = Number(item.price) || 0;
    const discount = Number(item.discount) || 0;
    const subtotal = Number(item.subtotal) || 0;

    // Get display name from product.name object (multi-language)
    const displayName =
      item.displayName ||
      (i18n.language === "ar"
        ? item.product.name?.ar
        : item.product.name?.en) ||
      t("Product");

    // Get first image from product.images array
    const displayImage = item.displayImage || item.product?.images?.[0] || "";

    const isAvailable = item.isAvailable ?? stock > 0;
    const isLowStock = item.isLowStock ?? (stock > 0 && stock <= 5);
    const canEditVariation = !!(
      item.variationSnapshot ||
      item.variation ||
      item.product?.hasVariations
    );

    const handleQuantityChange = useCallback(
      (delta: number) => {
        const newQty = localQuantity + delta;

        if (newQty >= 1 && newQty <= stock) {
          setLocalQuantity(newQty);
          onUpdateQuantity(item.id, delta);
        } else if (newQty === 0) {
          onUpdateQuantity(item.id, delta);
        }
      },
      [localQuantity, stock, item.id, onUpdateQuantity],
    );

    return (
      <div
        className={`cart-item-card bg-white rounded-2xl shadow-lg p-6 relative animate-slide-in-up ${
          isRemoving ? "opacity-0 scale-95" : "opacity-100 scale-100"
        }`}
        style={{ animationDelay: `${index * 0.05}s` }}
      >
        {!isAvailable || localQuantity === stock ? (
          <div className="absolute top-4 right-4 bg-gradient-to-r from-red-500 to-red-600 text-white text-xs font-bold px-4 py-2 rounded-full flex items-center gap-2 shadow-lg">
            <AlertCircle size={14} />
            {t("Out of Stock")}
          </div>
        ) : (
          isLowStock &&
          isAvailable && (
            <div className="absolute top-4 right-4 bg-gradient-to-r from-orange-500 to-orange-600 text-white text-xs font-bold px-4 py-2 rounded-full flex items-center gap-2 animate-pulse shadow-lg">
              <TrendingUp size={14} />
              {t("Low Stock")} ({stock} {t("left")})
            </div>
          )
        )}

        <div className="flex flex-col sm:flex-row gap-6">
          <div className="flex-shrink-0">
            <ImageWithFallback
              src={displayImage}
              alt={displayName}
              className="w-32 h-32 rounded-xl object-cover shadow-md"
            />
          </div>

          <div className="flex-1 min-w-0">
            <h3 className="text-xl font-bold text-gray-900 mb-2 truncate">
              {displayName}
            </h3>

            <VariationDisplay
              variation={item.variationSnapshot || item.variation}
              onEdit={
                onEditVariation && canEditVariation
                  ? () => onEditVariation(item)
                  : undefined
              }
            />

            {item.product?.sku && (
              <p className="text-xs text-gray-500 mb-2">
                {t("SKU")}: {item.product.sku}
              </p>
            )}

            <div className="flex items-center gap-2 mb-4 flex-wrap">
              {isAvailable && localQuantity !== stock ? (
                <span className="flex items-center gap-1.5 text-sm text-green-600 bg-green-50 px-3 py-1 rounded-full font-medium">
                  <CheckCircle2 size={16} />
                  {t("In Stock")}
                </span>
              ) : (
                <span className="flex items-center gap-1.5 text-sm text-red-600 bg-red-50 px-3 py-1 rounded-full font-medium">
                  <AlertCircle size={16} />
                  {t("Out of Stock")}
                </span>
              )}

              <span className="text-sm text-gray-600">
                <Price amount={price} />
              </span>
            </div>

            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div className="flex items-center bg-gray-100 rounded-xl overflow-hidden shadow-inner">
                <button
                  onClick={() => handleQuantityChange(-1)}
                  disabled={!isAvailable || localQuantity <= 1 || isUpdating}
                  className="p-3 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  aria-label={t("Decrease quantity")}
                >
                  <Minus size={18} className="text-gray-700" />
                </button>
                <span className="px-6 font-bold text-gray-900 min-w-[60px] text-center">
                  {localQuantity}
                </span>
                <button
                  onClick={() => handleQuantityChange(1)}
                  disabled={
                    !isAvailable || localQuantity >= stock || isUpdating
                  }
                  className="p-3 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  aria-label={t("Increase quantity")}
                >
                  <Plus size={18} className="text-gray-700" />
                </button>
              </div>

              <div className="flex items-center gap-4">
                <div className="text-right">
                  <p className="text-2xl font-bold text-gray-900">
                    <Price amount={subtotal} />
                  </p>
                  {discount > 0 && (
                    <p className="text-sm text-green-600 flex items-center gap-1 mt-1">
                      <Gift size={14} />
                      {t("Save")} <Price amount={discount} />
                    </p>
                  )}
                </div>

                <button
                  onClick={() => onRemove(item.id)}
                  disabled={isRemoving || isUpdating}
                  className="text-red-600 p-3 hover:bg-red-50 rounded-xl transition-colors disabled:opacity-50"
                  aria-label={t("Remove item")}
                >
                  <Trash2 size={20} />
                </button>
              </div>
            </div>
          </div>
        </div>

        {isUpdating && (
          <div className="absolute inset-0 bg-white/60 rounded-2xl flex items-center justify-center pointer-events-none">
            <div className="w-8 h-8 border-4 border-gray-300 border-t-gray-700 rounded-full animate-spin" />
          </div>
        )}
      </div>
    );
  },
);

/* ==================== CART PAGE ==================== */
const CartPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { success, err } = useToast();

  const {
    cart,
    loading,
    updatingItemId,
    removingItemId,
    destination,
    loadCart,
    updateQuantity,
    removeItem,
    clearCart,
    applyPromoCode,
    removePromoCode,
    setDestination,
    shippingLoading,
    shippingEstimatedDays,
  } = useCart();

  const [promoCode, setPromoCode] = useState("");
  const [promoError, setPromoError] = useState("");
  // Gift-related state
  const [isGift, setIsGift] = useState(false);
  const [recipientInfo, setRecipientInfo] = useState({
    recipientName: "",
    recipientEmail: "",
    recipientPhone: "",
  });
  const [giftMessage, setGiftMessage] = useState("");
  const [showGiftOption, ShowGiftOption] = useState(true);
  const [showGiftModal, setShowGiftModal] = useState(false);
  const [applyingPromo, setApplyingPromo] = useState(false);
  const [confirmConfig, setConfirmConfig] = useState({
    isOpen: false,
    title: "",
    message: "",
    confirmText: t("Confirm"),
    onConfirm: () => {},
  });
  const [isEditVariationOpen, setIsEditVariationOpen] = useState(false);
  const [editItem, setEditItem] = useState<any | null>(null);
  const [editProductDetails, setEditProductDetails] = useState<any | null>(
    null,
  );
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  const [editSelectedAttributes, setEditSelectedAttributes] = useState<
    Record<string, string>
  >({});
  const [isUpdatingVariation, setIsUpdatingVariation] = useState(false);

  const initialize = async () => {
    try {
      const addressRes = await BaseApi.get("/addresses/default");

      let destinationName = "";

      if (addressRes?.success && addressRes.data?.country) {
        const matchedCountry = COUNTRY_CODES.find(
          (c) => c.country === addressRes.data.country,
        );
        if (matchedCountry) {
          destinationName = matchedCountry.name;
          setDestination(destinationName);
        }
      }

      await loadCart(destinationName);
    } catch (e) {
      console.error("Address fetch failed", e);
    }
  };

  //   useEffect(() => {
  //   if (destination) {
  //     loadCart(destination);
  //   }
  // }, [destination]);

  useEffect(() => {
    initialize();
  }, [loadCart]);

  // Memoize expensive calculations
  const totals = useMemo(() => {
    return {
      subtotal: Number(cart.subtotal || 0),
      discount: Number(cart.discount || 0),
      tax: Number(cart.tax || 0),
      shipping: Number(cart.shipping || 0),
      total: Number(cart.total || 0),
    };
  }, [cart]);

  const shippingInfo = useMemo(
    () => calculateShippingProgress(totals.subtotal),
    [totals.subtotal],
  );

  const openEditVariation = async (item: any) => {
    setIsEditVariationOpen(true);
    setEditItem(item);
    setEditProductDetails(null);
    setEditError(null);
    setEditLoading(true);

    const initialAttributes = extractVariationAttributes(
      item.variationSnapshot || item.variation,
    );
    setEditSelectedAttributes(initialAttributes);

    try {
      const productId = item.productId || item.product?.id;
      if (!productId) {
        throw new Error("Missing product ID");
      }
      const res = await BaseApi.get(`/products/${productId}?isActive=true`);
      const detail = res?.data?.product || res?.data;
      if (detail) {
        setEditProductDetails(detail);
        const currentVariationId = extractVariationId(
          item.variationSnapshot || item.variation,
        );
        if (currentVariationId && detail?.variations?.length) {
          const matched = detail.variations.find(
            (variation: any) =>
              variation.id === currentVariationId ||
              variation._id === currentVariationId,
          );
          if (matched?.attributes) {
            setEditSelectedAttributes({
              ...initialAttributes,
              ...matched.attributes,
            });
          }
        }
      } else {
        setEditError(t("Failed to load product options"));
      }
    } catch (error: any) {
      console.error("Failed to load product details:", error);
      setEditError(error?.message || t("Failed to load product options"));
    } finally {
      setEditLoading(false);
    }
  };

  const closeEditVariation = () => {
    setIsEditVariationOpen(false);
    setEditItem(null);
    setEditProductDetails(null);
    setEditSelectedAttributes({});
    setEditError(null);
    setIsUpdatingVariation(false);
  };

  const handleEditAttributeClick = (type: string, value: string) => {
    setEditSelectedAttributes((prev) => ({ ...prev, [type]: value }));
  };

  const handleUpdateVariation = async () => {
    if (!editItem) return;
    if (editLoading || editError) return;

    if (requiresEditSelection && !selectedEditVariation) {
      err(t("Please select options"));
      return;
    }

    if (isEditOutOfStock) {
      err(t("Out of Stock"));
      return;
    }

    setIsUpdatingVariation(true);

    try {
      const variationId =
        selectedEditVariation?.id || selectedEditVariation?._id;
      const productId = editItem.productId || editItem.product?.id;
      if (!variationId) {
        throw new Error(t("Please select options"));
      }
      if (!productId) {
        throw new Error(t("Missing product information"));
      }

      const currentVariationId = extractVariationId(
        editItem.variationSnapshot || editItem.variation,
      );
      if (currentVariationId && currentVariationId === variationId) {
        success(t("Variation updated"));
        closeEditVariation();
        return;
      }

      const attributePayload: Record<string, string> = {};
      editVariationKeys.forEach((key) => {
        const value = editSelectedAttributes[key];
        if (value) {
          attributePayload[key] = value;
        }
      });

      const removeRes = await BaseApi.delete(`/cart/items/${editItem.id}`);
      if (!removeRes?.success) {
        throw new Error(removeRes?.message || "Failed to remove old item");
      }

      const addRes = await BaseApi.post("/cart/items", {
        productId,
        variationId,
        quantity: editItem.quantity,
        ...attributePayload,
      });

      if (!addRes?.success) {
        throw new Error(addRes?.message || "Failed to add variation");
      }

      success(t("Variation updated"));
      await loadCart(destination);
      closeEditVariation();
    } catch (error: any) {
      console.error("Failed to update variation:", error);
      err(error?.message || t("Failed to update variation"));
    } finally {
      setIsUpdatingVariation(false);
    }
  };

  const allowedEditVariationTypes = useMemo(
    () =>
      (editProductDetails?.variationTypes || [])
        .map((type: string) => type?.toLowerCase?.())
        .filter(Boolean),
    [editProductDetails?.variationTypes],
  );

  const editVariationOptions = useMemo(() => {
    const configuredTypes = allowedEditVariationTypes.filter(
      (type: string | undefined): type is string =>
        typeof type === "string" && type.length > 0,
    );
    const fallbackTypes = Array.from(
      new Set(
        (editProductDetails?.variations || []).flatMap((variation: any) =>
          Object.entries(variation.attributes || {})
            .filter(([, value]) => Boolean(value))
            .map(([key]) => key),
        ),
      ),
    );
    const variationTypesToUse =
      configuredTypes.length > 0 ? configuredTypes : fallbackTypes;
    const optionsMap: Record<string, Set<string>> = {};
    variationTypesToUse.forEach((type: string) => {
      optionsMap[type] = new Set();
    });

    (editProductDetails?.variations || []).forEach((variation: any) => {
      variationTypesToUse.forEach((type: string) => {
        const value = variation.attributes?.[type];
        if (value) {
          optionsMap[type].add(value as string);
        }
      });
    });

    return Object.fromEntries(
      Object.entries(optionsMap)
        .map(([key, set]) => [key, Array.from(set)] as const)
        .filter(([, values]) => values.length > 0),
    );
  }, [allowedEditVariationTypes, editProductDetails?.variations]);

  const editVariationKeys = Object.keys(editVariationOptions);
  const requiresEditSelection =
    (editProductDetails?.variations || []).length > 0 &&
    editVariationKeys.length > 0;

  const selectedEditVariation = useMemo(() => {
    if (!requiresEditSelection) return null;
    if (!editVariationKeys.every((key) => editSelectedAttributes[key])) {
      return null;
    }

    return (editProductDetails?.variations || []).find((variation: any) =>
      editVariationKeys.every(
        (key) => variation.attributes?.[key] === editSelectedAttributes[key],
      ),
    );
  }, [
    requiresEditSelection,
    editVariationKeys,
    editSelectedAttributes,
    editProductDetails?.variations,
  ]);

  const isEditOutOfStock = requiresEditSelection
    ? !!selectedEditVariation && selectedEditVariation.stock <= 0
    : false;

  const handleUpdateQuantity = useCallback(
    async (itemId: string, delta: number) => {
      const result = await updateQuantity(itemId, delta);
      if (result?.shouldRemove) {
        handleRemoveItem(itemId);
      }
    },
    [updateQuantity],
  );

  const handleRemoveItem = useCallback(
    (itemId: string) => {
      const item = cart.items.find((i) => i.id === itemId);
      const itemName =
        item?.displayName ||
        item?.product?.name?.en ||
        item?.product?.name?.ar ||
        t("Item");

      setConfirmConfig({
        isOpen: true,
        title: t("Remove Item"),
        message: t(
          "Are you sure you want to remove {{itemName}} from your cart?",
          { itemName },
        ),
        confirmText: t("Remove"),
        onConfirm: () => {
          removeItem(itemId);
          setConfirmConfig((c) => ({ ...c, isOpen: false }));
        },
      });
    },
    [cart.items, t, removeItem],
  );

  const handleClearCart = useCallback(() => {
    setConfirmConfig({
      isOpen: true,
      title: t("Clear Cart"),
      message: t("Are you sure you want to remove all items from your cart?"),
      confirmText: t("Clear All"),
      onConfirm: () => {
        clearCart();
        setConfirmConfig((c) => ({ ...c, isOpen: false }));
      },
    });
  }, [t, clearCart]);

  const handleApplyPromo = useCallback(async () => {
    if (!promoCode.trim()) {
      setPromoError(t("Please enter a promo code"));
      return;
    }

    setApplyingPromo(true);
    setPromoError("");

    const result = await applyPromoCode(promoCode.trim());
    if (!result.success) {
      setPromoError(result.error || t("Failed to apply promo code"));
    } else {
      setPromoCode("");
      success(t("Promo code applied successfully!"));
    }

    setApplyingPromo(false);
  }, [promoCode, t, applyPromoCode, success]);

  const handlePromoInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setPromoCode(e.target.value.toUpperCase());
      setPromoError("");
    },
    [],
  );

  // Handle gift modal success - save gift data
  const handleGiftSuccess = (giftData: GiftModalFormData) => {
    // console.log("Gift Data -> ", giftData);

    if (!cart.items.length) {
      err(t("Your cart is empty"));
      return;
    }

    const nextIsGift = true;

    const nextRecipientInfo = {
      recipientName: giftData.recipientName || "",
      recipientEmail: giftData.recipientEmail || "", // from cart / user
      recipientPhone: giftData.recipientPhone || "",
    };

    const nextGiftMessage = giftData.message || "";

    setIsGift(nextIsGift);
    setRecipientInfo(nextRecipientInfo);
    setGiftMessage(nextGiftMessage);

    setShowGiftModal(false);

    navigate("/checkout", {
      state: {
        isGift: nextIsGift,
        recipientInfo: nextRecipientInfo,
        giftMessage: nextGiftMessage,
      },
    });
  };

  const handleCheckout = useCallback(() => {
    if (!cart.items.length) {
      err(t("Your cart is empty"));
      return;
    }

    const hasUnavailableItems = cart.items.some(
      (item) => item.isAvailable === false,
    );
    if (hasUnavailableItems) {
      err(t("Please remove unavailable items before checkout"));
      return;
    }

    navigate("/checkout", {
      state: {
        isGift,
        recipientInfo,
        giftMessage,
      },
    });
  }, [navigate, cart.items, err, t]);

  const handleContinueShopping = useCallback(() => {
    navigate("/home");
  }, [navigate]);

  if (loading && !cart.items?.length) {
    return <LoadingComponent title={t("Cart")} />;
  }

  const itemCount = cart.items?.length || 0;

  return (
    <div className="min-h-screen bg-[#f2e6db]">
      <style>{`
        @keyframes slideInUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes scaleIn { from { opacity: 0; transform: scale(0.95); } to { opacity: 1; transform: scale(1); } }
        .animate-slide-in-up { animation: slideInUp 0.5s ease-out; }
        .animate-fade-in { animation: fadeIn 0.5s ease-out; }
        .animate-scale-in { animation: scaleIn 0.4s cubic-bezier(0.16, 1, 0.3, 1); }
        .progress-bar { transition: width 0.5s ease-out; }
        .cart-item-card { transition: all 0.3s ease; }
        .cart-item-card:hover { transform: translateY(-2px); box-shadow: 0 12px 24px rgba(0, 0, 0, 0.1); }
      `}</style>

      <div className="max-w-7xl mx-auto px-4 py-4 lg:py-8">
        <div className="mb-6 lg:mb-8 animate-slide-in-up">
          <button
            onClick={handleContinueShopping}
            className="group flex items-center gap-2 text-gray-600 hover:text-black mb-4 transition-all"
          >
            <div className="bg-white p-2 rounded-full shadow-sm group-hover:shadow-md transition-shadow">
              <ArrowLeft
                size={20}
                className="group-hover:-translate-x-1 transition-transform"
              />
            </div>
            <span className="font-semibold">{t("Continue Shopping")}</span>
          </button>

          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-2">
                {t("Shopping Cart")}
              </h1>
              <p className="text-gray-600 flex items-center gap-2">
                <Package2 size={18} />
                <span>
                  {itemCount === 0
                    ? t("No items")
                    : itemCount === 1
                      ? t("1 item in your cart")
                      : t("Items In Cart", { totalItems: itemCount })}
                </span>
              </p>
            </div>

            {itemCount > 0 && (
              <button
                onClick={handleClearCart}
                className="hidden sm:flex items-center gap-2 px-4 py-2 text-red-600 hover:text-white hover:bg-red-600 border-2 border-red-600 rounded-lg font-semibold transition-all active:scale-95"
              >
                <Trash2 size={18} />
                <span>{t("Clear Cart")}</span>
              </button>
            )}
          </div>
        </div>

        {itemCount === 0 ? (
          <div className="bg-white rounded-2xl shadow-xl p-8 lg:p-16 text-center animate-scale-in">
            <div className="max-w-md mx-auto">
              <div className="relative mb-8">
                <div className="bg-gradient-to-br from-gray-100 to-gray-200 rounded-full w-40 h-40 flex items-center justify-center mx-auto">
                  <ShoppingCart size={80} className="text-gray-400" />
                </div>
                <div className="absolute -top-2 -right-2 bg-yellow-400 rounded-full p-3 animate-bounce">
                  <Sparkles size={24} className="text-yellow-600" />
                </div>
              </div>
              <h2 className="text-3xl font-bold text-gray-900 mb-3">
                {t("Your cart is empty")}
              </h2>
              <p className="text-gray-600 mb-8 text-lg">
                {t(
                  "Looks like you haven't added anything to your cart yet. Start shopping to fill it up!",
                )}
              </p>
              <button
                onClick={handleContinueShopping}
                className="bg-gradient-to-r from-black to-gray-700 hover:from-gray-800 hover:to-black text-white font-bold px-10 py-4 rounded-xl flex items-center gap-3 mx-auto transition-all transform hover:scale-105 active:scale-95 shadow-lg"
              >
                <ShoppingCart size={22} />
                <span>{t("Start Shopping")}</span>
                <ChevronRight size={22} />
              </button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
            <div className="lg:col-span-2 space-y-6">
              {/* Shipping Progress */}
              {/* {!shippingInfo.isFreeShipping && (
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-6 shadow-sm border border-[#0d223f] animate-slide-in-up">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Truck size={20} className="text-blue-600" />
                      <span className="font-semibold text-gray-900">
                        {t("Free Shipping Progress")}
                      </span>
                    </div>
                    <span className="text-sm font-bold text-blue-600">
                      {Math.round(shippingInfo.progress)}%
                    </span>
                  </div>
                  <div className="relative h-3 bg-white rounded-full overflow-hidden mb-2">
                    <div
                      className="progress-bar absolute left-0 top-0 h-full bg-gradient-to-r from-gray-700 to-black rounded-full"
                      style={{ width: `${shippingInfo.progress}%` }}
                    />
                  </div>
                  <p className="text-sm text-gray-700">
                    {t("Add")}{" "}
                    <span className="font-bold text-blue-600">
                      <Price amount={Number(shippingInfo.remaining)} />
                    </span>{" "}
                    {t("more to get FREE shipping!")}
                  </p>
                </div>
              )} */}

              {/* Cart Items */}
              <div className="space-y-4">
                {cart &&
                  cart.items &&
                  cart.items.map((item, index) => (
                    <CartItem
                      key={item.id}
                      item={item}
                      index={index}
                      isRemoving={removingItemId === item.id}
                      isUpdating={updatingItemId === item.id}
                      onUpdateQuantity={handleUpdateQuantity}
                      onRemove={handleRemoveItem}
                      onEditVariation={openEditVariation}
                    />
                  ))}
              </div>
              {/* ADD THIS GIFT BUTTON */}
              {showGiftOption && (
                <button
                  onClick={() => setShowGiftModal(true)}
                  className={`w-full ${
                    isGift
                      ? "bg-gradient-to-r from-gray-700 to-black"
                      : "bg-gradient-to-r from-gray-700 to-black hover:from-gray-600 hover:to-gray-700"
                  } text-white font-bold py-4 px-6 rounded-xl flex items-center justify-center gap-2 transition-all shadow-lg hover:shadow-2xl transform hover:scale-105 active:scale-95`}
                >
                  <Gift size={20} />
                  {isGift ? t("✓ Gift Details Added") : t("Send as Gift")}
                </button>
              )}

              {/* Gift Summary */}
              {isGift && (
                <div className="bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 rounded-lg p-4 mt-4">
                  <div className="flex items-center gap-2 text-purple-700 font-semibold mb-2">
                    <Gift size={18} />
                    <span>{t("Gift Order")}</span>
                  </div>
                  <div className="text-sm text-gray-700 space-y-1">
                    <p>
                      <strong>{t("Recipient")}:</strong>{" "}
                      {recipientInfo.recipientName}
                    </p>
                    {recipientInfo.recipientPhone && (
                      <p>
                        <strong>{t("Phone")}:</strong>{" "}
                        {recipientInfo.recipientPhone}
                      </p>
                    )}
                    {recipientInfo.recipientEmail && (
                      <p>
                        <strong>{t("Email")}:</strong>{" "}
                        {recipientInfo.recipientEmail}
                      </p>
                    )}
                    {giftMessage && (
                      <p className="mt-2 italic text-gray-600">
                        "{giftMessage}"
                      </p>
                    )}
                  </div>
                </div>
              )}

              <GiftSendModal
                isOpen={showGiftModal}
                onClose={() => setShowGiftModal(false)}
                onSuccess={handleGiftSuccess}
              />

              {/* Promo Code */}
              <div className="bg-gradient-to-br rounded-2xl shadow-lg p-6 border border-black-100">
                <div className="flex items-center gap-3 mb-4">
                  <div className="bg-gradient-to-r bg-[#8A1538] p-2 rounded-lg">
                    <Tag size={20} className="text-white" />
                  </div>
                  <span className="text-lg font-bold text-gray-900">
                    {t("Have a coupon code?")}
                  </span>
                </div>
                <div className="flex gap-3">
                  <input
                    type="text"
                    value={promoCode}
                    onChange={handlePromoInputChange}
                    placeholder={t("Enter promo code")}
                    className="flex-1 px-4 py-3 border-2 border-purple-200 rounded-xl focus:border-purple-500 focus:outline-none font-semibold uppercase"
                    disabled={applyingPromo || !!cart.discountCode}
                  />
                  <button
                    onClick={handleApplyPromo}
                    disabled={
                      applyingPromo || !promoCode.trim() || !!cart.discountCode
                    }
                    className="bg-gradient-to-r bg-[#8A1538] text-white font-bold px-4 py-3 rounded-xl transition-all disabled:opacity-50 shadow-lg active:scale-95"
                  >
                    {applyingPromo ? (
                      <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      t("Apply")
                    )}
                  </button>
                </div>
                {promoError && (
                  <div className="mt-3 flex items-center gap-2 bg-red-50 text-red-700 px-4 py-3 rounded-xl border border-red-200 animate-slide-in-up">
                    <AlertCircle size={18} />
                    <span className="text-sm font-medium">{promoError}</span>
                  </div>
                )}
                {cart.discountCode && (
                  <div className="mt-3 flex items-center justify-between bg-gradient-to-r from-green-50 to-emerald-50 text-green-700 px-4 py-3 rounded-xl border border-green-200 animate-slide-in-up">
                    <div className="flex items-center gap-2">
                      <CheckCircle2 size={18} />
                      <span className="text-sm font-semibold">
                        {t("Code Applied")}:{" "}
                        <strong>{cart.discountCode}</strong>
                      </span>
                    </div>
                    <button
                      onClick={removePromoCode}
                      className="text-green-700 hover:text-green-900 transition-colors"
                      aria-label={t("Remove promo code")}
                    >
                      <X size={18} />
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Order Summary */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-2xl shadow-xl p-6 sticky top-4 animate-scale-in border border-gray-100">
                <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                  <Sparkles size={24} className="text-yellow-500" />
                  {t("Order Summary")}
                </h2>

                <div className="space-y-4 mb-6">
                  <div className="flex items-center justify-between text-gray-700 text-lg">
                    <span>{t("Subtotal")}</span>
                    <span className="font-bold">
                      <Price amount={totals.subtotal} />
                    </span>
                  </div>
                  {totals.discount > 0 && (
                    <div className="flex items-center justify-between text-green-600 text-lg bg-green-50 px-4 py-2 rounded-lg">
                      <div className="flex items-center gap-2">
                        <Gift size={18} />
                        <span className="font-semibold">{t("Discount")}</span>
                      </div>
                      <span className="font-bold">
                        - <Price amount={totals.discount} />
                      </span>
                    </div>
                  )}
                  <div className="flex items-center justify-between text-gray-700 text-lg">
                    <div className="flex flex-col">
                      <span className="text-gray-600">{t("Shipping")}:</span>
                      {shippingEstimatedDays && (
                        <span className="text-xs text-gray-500">
                          {t("Estimated Delivery")}: {shippingEstimatedDays}
                        </span>
                      )}
                    </div>
                    {shippingLoading ? (
                      <span className="inline-flex items-center gap-2 text-gray-500 text-sm font-medium">
                        <span className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                        <span>{t("Calculating...")}</span>
                      </span>
                    ) : totals.shipping === 0 ? (
                      <span className="flex items-center gap-1 text-green-600 font-bold">
                        <CheckCircle2 size={18} />
                        {t("FREE")}
                      </span>
                    ) : (
                      <span className="font-bold">
                        <Price amount={totals.shipping} />
                      </span>
                    )}
                  </div>
                  <div className="flex items-center justify-between text-gray-700 text-lg">
                    <span>{t("Tax")}</span>
                    <span className="font-bold">
                      <Price amount={totals.tax} />
                    </span>
                  </div>
                  <div className="border-t-2 border-gray-200 pt-4 mt-4">
                    <div className="flex items-center justify-between">
                      <span className="text-xl font-bold text-gray-900">
                        {t("Total")}
                      </span>
                      <div className="text-right">
                        <span className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                          <Price amount={Number(totals.total || cart.total)} />
                        </span>
                        <p className="text-xs text-gray-500 mt-1">
                          {t("Including tax")}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <button
                  onClick={handleCheckout}
                  className="w-full bg-gradient-to-r from-black to-gray-700 hover:from-gray-800 hover:to-black text-white font-bold py-5 px-6 rounded-xl flex items-center justify-center gap-3 mb-4 transition-all shadow-lg hover:shadow-2xl transform hover:scale-105 active:scale-95"
                >
                  <Lock size={22} />
                  <span className="text-lg">{t("Secure Checkout")}</span>
                  <ChevronRight size={22} />
                </button>

                {/* Trust Badges */}
                <div className="grid grid-cols-3 gap-3 mb-6">
                  <div className="text-center p-3 bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl border border-green-100">
                    <Lock size={20} className="mx-auto mb-2 text-green-600" />
                    <span className="text-xs font-semibold text-green-700">
                      {t("Secure")}
                    </span>
                  </div>
                  <div className="text-center p-3 bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl border border-blue-100">
                    <RefreshCw
                      size={20}
                      className="mx-auto mb-2 text-blue-600"
                    />
                    <span className="text-xs font-semibold text-blue-700">
                      {t("Returns")}
                    </span>
                  </div>
                  <div className="text-center p-3 bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl border border-purple-100">
                    <Truck size={20} className="mx-auto mb-2 text-purple-600" />
                    <span className="text-xs font-semibold text-purple-700">
                      {t("Fast")}
                    </span>
                  </div>
                </div>

                {/* Payment Methods */}
                <div className="text-center bg-gray-50 rounded-xl p-4 border border-gray-200">
                  <p className="text-sm font-semibold text-gray-700 mb-3">
                    {t("We accept")}
                  </p>
                  <div className="flex items-center justify-center gap-4">
                    <img
                      src={googlePay}
                      alt="Google Pay"
                      className="h-8 object-contain filter hover:saturate-150 transition-all"
                    />
                    <img
                      src={applePay}
                      alt="Apple Pay"
                      className="h-8 object-contain filter hover:saturate-150 transition-all"
                    />
                    <img
                      src={Mastercard}
                      alt="Mastercard"
                      className="h-8 object-contain filter hover:saturate-150 transition-all"
                    />
                    <img
                      src={Visa}
                      alt="Visa"
                      className="h-8 object-contain filter hover:saturate-150 transition-all"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <ConfirmModal
        isOpen={confirmConfig.isOpen}
        title={confirmConfig.title}
        message={confirmConfig.message}
        confirmText={confirmConfig.confirmText}
        cancelText={t("Cancel")}
        icon={Trash2}
        iconColor="text-red-600"
        iconBg="bg-red-100"
        onConfirm={confirmConfig.onConfirm}
        onCancel={() => setConfirmConfig((c) => ({ ...c, isOpen: false }))}
      />

      {isEditVariationOpen && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden">
            <div className="flex items-start justify-between gap-4 px-6 py-5 border-b">
              <div>
                <h3 className="text-xl font-bold text-gray-900">
                  {t("Edit Variation")}
                </h3>
                {editItem && (
                  <p className="text-sm text-gray-500 mt-1">
                    {editItem.displayName ||
                      editItem.product?.name?.en ||
                      t("Product")}
                  </p>
                )}
              </div>
              <button
                className="text-gray-500 hover:text-gray-800"
                onClick={closeEditVariation}
                aria-label={t("Close")}
              >
                <X size={20} />
              </button>
            </div>

            <div className="px-6 py-5 max-h-[60vh] overflow-y-auto">
              {editLoading ? (
                <div className="flex items-center gap-3 text-gray-600">
                  <div className="w-5 h-5 border-2 border-gray-300 border-t-gray-800 rounded-full animate-spin" />
                  <span>{t("Loading options...")}</span>
                </div>
              ) : editError ? (
                <div className="text-red-600 bg-red-50 border border-red-200 rounded-lg p-3">
                  {editError}
                </div>
              ) : requiresEditSelection ? (
                <div className="space-y-4">
                  {Object.entries(editVariationOptions).map(
                    ([type, values]) => (
                      <div key={type}>
                        <p className="text-xs font-semibold text-gray-600 mb-2">
                          {t("SELECT")} {type.toUpperCase()}
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {values.map((value) => (
                            <button
                              key={`${type}-${value}`}
                              type="button"
                              className={`px-3 py-2 rounded-lg text-sm font-medium border transition-all ${
                                editSelectedAttributes[type] === value
                                  ? "bg-black text-white border-black"
                                  : "bg-gray-100 text-gray-700 border-transparent hover:bg-gray-200"
                              }`}
                              onClick={() =>
                                handleEditAttributeClick(type, value)
                              }
                            >
                              {value}
                            </button>
                          ))}
                        </div>
                      </div>
                    ),
                  )}
                </div>
              ) : (
                <div className="text-sm text-gray-600">
                  {t("No options available for this product.")}
                </div>
              )}
            </div>

            <div className="px-6 py-5 border-t flex items-center justify-end gap-3">
              <button
                type="button"
                className="px-4 py-2 rounded-lg bg-gray-100 text-gray-700 font-semibold hover:bg-gray-200"
                onClick={closeEditVariation}
              >
                {t("Cancel")}
              </button>
              <button
                type="button"
                className="px-5 py-2 rounded-lg bg-gradient-to-r from-black to-gray-700 text-white font-semibold hover:from-gray-800 hover:to-black disabled:opacity-60"
                onClick={handleUpdateVariation}
                disabled={
                  editLoading ||
                  isUpdatingVariation ||
                  !!editError ||
                  isEditOutOfStock ||
                  (requiresEditSelection && !selectedEditVariation)
                }
              >
                {isUpdatingVariation
                  ? t("Updating...")
                  : isEditOutOfStock
                    ? t("Out of Stock")
                    : t("Update Variation")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CartPage;
